/**
 * R-167 — tenant-level channel → source-code mapping section.
 *
 * Renders one row per ingestion channel (Batch.source value vocabulary:
 * `upload`, `scanner`, `email`, `directory`, `fax`, `api`). Tenants set
 * a short code per channel; the renderer reads it at write time when a
 * template includes the `{sourceCode}` token.
 *
 * Below the channel table sits a collapsed device-overrides section —
 * optional per-physical-scanner overrides for the scanner channel, keyed
 * by the device/agent name as reported by the channel that delivered the
 * batch. When present these win over the channel code (renderer falls back
 * from device-override → channel code → empty).
 *
 * NOTE (2026-08-07): there is no browser-scanning channel. Web TWAIN was
 * descoped for v1 (see docs/2026-08-07-feature-completeness-analysis.md §2);
 * scanner-originated documents arrive via the on-prem Pull Agent or a hot
 * folder and get stamped with the `scanner` channel code. The `scanner` key
 * itself stays stable — persisted tenant data references it.
 */
import { useEffect, useState } from "react";
import styles from "./tenant-settings.module.css";

interface ChannelDef {
  key: string;
  label: string;
  placeholder: string;
}

// The 6 channels match the `Batch.source` value vocabulary.
const CHANNELS: readonly ChannelDef[] = [
  { key: "upload", label: "Upload", placeholder: "UPL" },
  { key: "scanner", label: "Scanner (Pull Agent / hot folder)", placeholder: "SCN" },
  { key: "email", label: "Email", placeholder: "EML" },
  { key: "directory", label: "Network folder", placeholder: "DIR" },
  { key: "fax", label: "Fax", placeholder: "FAX" },
  { key: "api", label: "API", placeholder: "API" },
];

// Derive the per-channel state shape from the (nullable) tenant prop. Shared
// by the mount initializer and the R-180 re-sync effect so they can't drift.
function channelsFromProp(
  initial: Record<string, string> | null,
): Record<string, string> {
  return CHANNELS.reduce<Record<string, string>>((acc, c) => {
    acc[c.key] = initial?.[c.key] ?? "";
    return acc;
  }, {});
}

function devicesFromProp(
  initial: Record<string, string> | null,
): Array<{ name: string; code: string }> {
  return Object.entries(initial ?? {}).map(([name, code]) => ({ name, code }));
}

export interface SourceCodesSectionProps {
  initialChannelCodes: Record<string, string> | null;
  initialDeviceCodes: Record<string, string> | null;
  /**
   * May return a promise. When it does, the section disables the save
   * button + shows a saving spinner until it settles, then flashes a
   * "Saved" indicator (R-180). A void return keeps the synchronous
   * fire-and-forget behavior for callers that don't await persistence.
   */
  onSave: (payload: {
    channelSourceCodes: Record<string, string>;
    scannerDeviceCodes: Record<string, string>;
  }) => void | Promise<unknown>;
}

export default function SourceCodesSection({
  initialChannelCodes,
  initialDeviceCodes,
  onSave,
}: SourceCodesSectionProps) {
  const [channels, setChannels] = useState<Record<string, string>>(() =>
    channelsFromProp(initialChannelCodes),
  );
  const [devices, setDevices] = useState<Array<{ name: string; code: string }>>(
    () => devicesFromProp(initialDeviceCodes),
  );
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const detailsOpen =
    initialDeviceCodes != null && Object.keys(initialDeviceCodes).length > 0;

  // R-180 — the section mounts before `useCurrentTenant` resolves, so the
  // lazy useState initializers capture the pre-load `null` props ONCE. When
  // the tenant later loads, the props change but the initializer never
  // re-runs, stranding the inputs empty even though the DB has saved values.
  // These effects re-sync local state whenever the incoming props change.
  useEffect(() => {
    setChannels(channelsFromProp(initialChannelCodes));
  }, [initialChannelCodes]);

  useEffect(() => {
    setDevices(devicesFromProp(initialDeviceCodes));
  }, [initialDeviceCodes]);

  async function handleSave() {
    const deviceMap: Record<string, string> = {};
    for (const d of devices) {
      if (d.name.trim() && d.code.trim()) {
        deviceMap[d.name.trim()] = d.code.trim();
      }
    }
    setJustSaved(false);
    setSaving(true);
    try {
      await onSave({ channelSourceCodes: channels, scannerDeviceCodes: deviceMap });
      setJustSaved(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>Source Codes</h2>
      <p className={styles.overviewHint}>
        Map each ingestion channel to a short code. Templates can include{" "}
        <code>{`{sourceCode}`}</code> to emit it (e.g. NOW_20260527-SCN-0042).
        Leave blank to render an empty token.
      </p>

      <table className={styles.pendingTable} style={{ marginTop: "0.75rem" }}>
        <tbody>
          {CHANNELS.map((c) => (
            <tr key={c.key}>
              <td style={{ width: "12rem", fontWeight: 500 }}>{c.label}</td>
              <td>
                <input
                  id={`src-code-${c.key}`}
                  aria-label={c.label}
                  className={styles.textInput}
                  value={channels[c.key]}
                  maxLength={16}
                  onChange={(e) => {
                    setJustSaved(false);
                    setChannels((prev) => ({ ...prev, [c.key]: e.target.value }));
                  }}
                  placeholder={c.placeholder}
                  data-testid={`source-code-${c.key}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <details open={detailsOpen} style={{ marginTop: "1rem" }}>
        <summary
          style={{
            cursor: "pointer",
            fontWeight: 600,
            padding: "var(--space-2, 0.5rem) 0",
          }}
        >
          Scanner device overrides ({devices.length})
        </summary>
        <p className={styles.overviewHint}>
          Optional. Override the scanner channel code for specific physical
          scanners. Use the device or agent name as the delivering channel
          reports it — the Pull Agent&rsquo;s device name, or the hot-folder
          name for scanners that drop files.
        </p>
        <table className={styles.pendingTable}>
          <tbody>
            {devices.map((d, i) => (
              <tr key={i}>
                <td>
                  <input
                    className={styles.textInput}
                    placeholder="Device name (e.g. Brother MFC-1234)"
                    aria-label={`Device name ${i + 1}`}
                    value={d.name}
                    onChange={(e) => {
                      const copy = [...devices];
                      copy[i] = { ...copy[i]!, name: e.target.value };
                      setDevices(copy);
                    }}
                  />
                </td>
                <td>
                  <input
                    className={styles.textInput}
                    placeholder="Code (e.g. SS01)"
                    aria-label={`Device code ${i + 1}`}
                    value={d.code}
                    onChange={(e) => {
                      const copy = [...devices];
                      copy[i] = { ...copy[i]!, code: e.target.value };
                      setDevices(copy);
                    }}
                  />
                </td>
                <td style={{ width: "3rem" }}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    style={{ padding: "0.25rem 0.5rem" }}
                    onClick={() =>
                      setDevices(devices.filter((_, j) => j !== i))
                    }
                    aria-label={`Remove device ${i + 1}`}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={() => setDevices([...devices, { name: "", code: "" }])}
          style={{ marginTop: "0.5rem" }}
        >
          + Add device override
        </button>
      </details>

      <div
        className={styles.actionRow}
        style={{
          marginTop: "var(--space-4, 1rem)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3, 0.75rem)",
        }}
      >
        <button
          type="button"
          className={styles.primaryButton}
          onClick={handleSave}
          disabled={saving}
          aria-busy={saving}
          data-testid="source-codes-save"
        >
          {saving ? "Saving…" : "Save Source Codes"}
        </button>
        {justSaved && !saving && (
          <span
            role="status"
            data-testid="source-codes-saved"
            style={{ color: "var(--color-success, #4CAF50)", fontWeight: 600 }}
          >
            ✓ Saved
          </span>
        )}
      </div>
    </section>
  );
}
